import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CheckCheck, X } from "lucide-react";
import NotificationTypeIcon from "./NotificationTypeIcon";
import {
  formatCustomInLocale,
  formatIsoDateInDisplayZone,
} from "../../utils/datetime";
import "./NotificationsPanelView.scss";

/**
 * The header notifications dropdown, as it looks in every portal.
 *
 * Presentational only: each portal's NotificationsPanel keeps its own data logic (Redux slice,
 * mark-read / delete calls, where a click navigates, how titles are localised) and passes it in.
 *
 * The same file lives in tiryaq-doctor-pharmacy-app, tiryaq-lab-app, tiryaq-imaging-app,
 * tiryaq-nurse-app, tiryaq-physiotherapy-app and tiryaq-insurance-app
 * (src/components/notifications/), together with NotificationsPanelView.scss and
 * NotificationTypeIcon.jsx/.scss. Keep the copies identical so the window stays consistent.
 *
 * Props
 *   variant                  Optional. "dropdown" (default) floats under the bell and closes on
 *                            an outside click / Escape. "page" renders the same list in the
 *                            page flow (full width, no outside-click handling), for a
 *                            full Notifications page.
 *   onClose()                Called on a click outside the panel or on Escape (dropdown only).
 *   labels                   Translated strings: title, unreadCount(count), markAllAsRead,
 *                            loading, failedToLoad, retry, empty, emptyHint, delete,
 *                            loadMore, loadingMore.
 *   notifications            Rows to render.
 *   unreadCount              Number shown in the header pill.
 *   loading                  A fetch is in flight (skeleton when empty, progress bar otherwise).
 *   error                    The last fetch failed.
 *   markingAllRead           "Mark all as read" request in flight.
 *   hasMore                  Another page exists.
 *   onRetry / onLoadMore / onMarkAllAsRead
 *   getKey(n)                Stable React key.
 *   getTitle(n) / getMessage(n)
 *   isUnread(n)
 *   isClickable(n)           Optional, default true. Shows the pointer cursor.
 *   getVisual(n)             Optional icon override, e.g. { icon: "document", tone: "green" }.
 *   isHighlighted(n)         Optional, default false. Shows the row in red to draw attention
 *                            (e.g. a patient report shared by a center, in the doctor portal).
 *   onOpen(n) / onDelete(n)
 */

const DATE_FIELDS = [
  "createdAt",
  "created_at",
  "sentAt",
  "sent_at",
  "updatedAt",
  "updated_at",
  "timestamp",
];
const DAY_MS = 24 * 60 * 60 * 1000;
// Under a minute old reads "Just now".
const JUST_NOW_MS = 60 * 1000;

/**
 * Compact relative row timestamp plus the full date and time for the tooltip, both in the user's
 * display timezone. Null when the row has no usable date.
 *   under a minute  → "Just now"
 *   earlier today   → "Today"
 *   yesterday       → "Yesterday"
 *   2–6 days        → "3 days ago"
 *   7 days          → "1 week ago"
 *   older           → "Oct 15" ("Oct 15, 2025" in another year)
 */
export const getNotificationDate = (notification, language) => {
  const raw = DATE_FIELDS.map((field) => notification?.[field]).find(Boolean);
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return null;

  try {
    const locale = language === "ar" ? "ar-IQ" : "en-US";
    const full = formatCustomInLocale(date, locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    const capitalize = (text) =>
      text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);

    const now = Date.now();
    // Also covers a timestamp slightly in the future (device clock ahead of the server).
    if (now - date.getTime() < JUST_NOW_MS) {
      return { label: language === "ar" ? "الآن" : "Just now", full };
    }

    // Whole calendar days between the two dates in the display timezone. Both keys are
    // YYYY-MM-DD, so they parse as UTC midnights and the difference is an exact day count.
    const dayKey = formatIsoDateInDisplayZone(date);
    const todayKey = formatIsoDateInDisplayZone(now);
    const daysAgo = Math.round((Date.parse(todayKey) - Date.parse(dayKey)) / DAY_MS);

    if (daysAgo <= 6) {
      // "today" / "yesterday" / "3 days ago" (Arabic: "اليوم" / "أمس" / "قبل ٣ أيام").
      const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
      return { label: capitalize(relative.format(-Math.max(daysAgo, 0), "day")), full };
    }
    if (daysAgo === 7) {
      // numeric "always" gives "1 week ago" rather than "last week".
      const relative = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
      return { label: capitalize(relative.format(-1, "week")), full };
    }
    return {
      label: formatCustomInLocale(date, locale, {
        year: dayKey.slice(0, 4) === todayKey.slice(0, 4) ? undefined : "numeric",
        month: "short",
        day: "numeric",
      }),
      full,
    };
  } catch (error) {
    console.warn("Error formatting notification date:", error, notification);
    return null;
  }
};

const SKELETON_ROWS = [0, 1, 2, 3];

const NotificationsPanelView = ({
  variant = "dropdown",
  onClose,
  labels,
  notifications = [],
  unreadCount = 0,
  loading = false,
  error = false,
  markingAllRead = false,
  hasMore = false,
  onRetry,
  onLoadMore,
  onMarkAllAsRead,
  getKey,
  getTitle,
  getMessage,
  isUnread,
  isClickable,
  getVisual,
  isHighlighted,
  onOpen,
  onDelete,
}) => {
  const { i18n } = useTranslation();
  const panelRef = useRef(null);
  const isPage = variant === "page";

  // Close on a click outside the panel or on Escape (the dropdown only; a page stays put).
  useEffect(() => {
    if (isPage) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, isPage]);

  const hasRows = notifications.length > 0;
  const hasUnread = unreadCount > 0 || notifications.some((n) => isUnread(n));
  const isInitialLoad = loading && !hasRows;
  const isEmpty = !loading && !error && !hasRows;

  return (
    <div
      ref={panelRef}
      className={`ntf${isPage ? " ntf--page" : ""}`}
      role={isPage ? "region" : "dialog"}
      aria-label={labels.title}
    >
      <div className="ntf__header">
        <div className="ntf__heading">
          <h2 className="ntf__title">{labels.title}</h2>
          {unreadCount > 0 && (
            <span className="ntf__count">
              {labels.unreadCount(unreadCount > 99 ? "99+" : unreadCount)}
            </span>
          )}
        </div>
        {hasRows && hasUnread && onMarkAllAsRead && (
          <button
            type="button"
            className="ntf__mark-all"
            onClick={onMarkAllAsRead}
            disabled={markingAllRead}
          >
            <CheckCheck size={16} aria-hidden="true" />
            <span>{labels.markAllAsRead}</span>
          </button>
        )}
        {loading && hasRows && (
          <span className="ntf__progress" aria-hidden="true" />
        )}
      </div>

      <div className="ntf__list" aria-busy={loading}>
        {isInitialLoad && (
          <div
            className="ntf__skeletons"
            role="status"
            aria-label={labels.loading}
          >
            {SKELETON_ROWS.map((i) => (
              <div key={i} className="ntf__skeleton">
                <span className="ntf__skeleton-circle" />
                <span className="ntf__skeleton-lines">
                  <span />
                  <span />
                </span>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="ntf__state" role="alert">
            <p className="ntf__state-title">{labels.failedToLoad}</p>
            {onRetry && (
              <button type="button" className="ntf__retry" onClick={onRetry}>
                {labels.retry}
              </button>
            )}
          </div>
        )}

        {isEmpty && (
          <div className="ntf__state">
            <span className="ntf__state-icon">
              <NotificationTypeIcon visual={{ icon: "bell", tone: "orange" }} />
            </span>
            <p className="ntf__state-title">{labels.empty}</p>
            <p className="ntf__state-hint">{labels.emptyHint}</p>
          </div>
        )}

        {hasRows && (
          <ul className="ntf__items">
            {notifications.map((notification) => {
              const unread = isUnread(notification);
              const clickable = isClickable ? isClickable(notification) : true;
              const highlighted = isHighlighted ? isHighlighted(notification) : false;
              const date = getNotificationDate(notification, i18n.language);
              return (
                <li
                  key={getKey(notification)}
                  className={`ntf__item${unread ? " ntf__item--unread" : ""}${
                    highlighted ? " ntf__item--alert" : ""
                  }`}
                >
                  <button
                    type="button"
                    className={`ntf__item-main${clickable ? " ntf__item-main--link" : ""}`}
                    onClick={() => onOpen(notification)}
                  >
                    <span className="ntf__item-icon">
                      <NotificationTypeIcon
                        notification={notification}
                        visual={getVisual ? getVisual(notification) : undefined}
                      />
                      {unread && <span className="ntf__dot" />}
                    </span>
                    <span className="ntf__item-body">
                      <span className="ntf__item-title">
                        {getTitle(notification)}
                      </span>
                      <span className="ntf__item-text">
                        {getMessage(notification)}
                      </span>
                    </span>
                  </button>

                  <div className="ntf__item-aside">
                    {date && (
                      <span className="ntf__item-time" title={date.full}>
                        {date.label}
                      </span>
                    )}
                    <button
                      type="button"
                      className="ntf__delete"
                      onClick={() => onDelete(notification)}
                      aria-label={labels.delete}
                      title={labels.delete}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {hasRows && hasMore && onLoadMore && (
          <div className="ntf__more">
            <button
              type="button"
              className="ntf__more-btn"
              onClick={onLoadMore}
              disabled={loading}
            >
              {loading ? labels.loadingMore : labels.loadMore}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanelView;
